import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Select, Space, Table, Typography } from 'antd';
import { gql, useMutation, useQuery } from '@apollo/client';
import Papa from 'papaparse';
import { UserContext } from 'contexts/UserContext';
import { useCorporateClients } from 'hooks/useCorporateClients';
import PermissionsAlert from 'components/PermissionsAlert/PermissionsAlert';

const PLANS = gql`
  query AddressImportPlans($communityId: Int!) {
    addressImportPlans(communityId: $communityId)
  }
`;
const IMPORT = gql`
  mutation AddressAccountImport(
    $communityId: Int!
    $levelId: Int!
    $rows: JSON!
    $apply: Boolean!
  ) {
    addressAccountImport(
      communityId: $communityId
      levelId: $levelId
      rows: $rows
      apply: $apply
    )
  }
`;
type Row = { Room: string; Building: string; Email: string };
type Result = {
  row: number;
  email: string;
  status: string;
  message?: string;
  userId?: number;
};

export default function AddressImport() {
  const { dbUser } = useContext(UserContext);
  const allowed =
    !!dbUser?.userPermissions.AdminLogin &&
    !!dbUser?.userPermissions.ManageUsers;
  const { corporateClients, getCorporateClients } = useCorporateClients();
  const [communityId, setCommunityId] = useState<number>();
  const [levelId, setLevelId] = useState<number>();
  const [rows, setRows] = useState<Row[]>([]);
  const [results, setResults] = useState<Result[]>([]);
  const [previewed, setPreviewed] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const revision = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const {
    data: plans,
    loading: plansLoading,
    error: plansError,
  } = useQuery(PLANS, {
    variables: { communityId },
    skip: !allowed || !communityId,
    fetchPolicy: 'network-only',
  });
  const [run, { loading }] = useMutation(IMPORT);
  useEffect(() => {
    if (allowed) getCorporateClients();
  }, [allowed, getCorporateClients]);
  const invalidate = () => {
    revision.current++;
    setPreviewed(false);
    setResults([]);
    setCompleted(false);
    setError('');
  };
  const load = async (file?: File) => {
    invalidate();
    setRows([]);
    const version = revision.current;
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setError('Choose a CSV file smaller than 1 MB.');
      return;
    }
    try {
      const parsed = Papa.parse<Row>(await file.text(), {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: (header) => header.trim(),
      });
      if (revision.current !== version) return;
      if (
        parsed.errors.length ||
        !['Room', 'Building', 'Email'].every((key) =>
          parsed.meta.fields?.includes(key),
        )
      )
        throw new Error('CSV must have Room, Building and Email columns.');
      if (!parsed.data.length || parsed.data.length > 1000)
        throw new Error('Upload between 1 and 1000 address rows.');
      setRows(
        parsed.data.map((row) => ({
          Room: row.Room || '',
          Building: row.Building || '',
          Email: row.Email || '',
        })),
      );
    } catch (e) {
      if (revision.current === version)
        setError(e instanceof Error ? e.message : 'Could not read CSV.');
    }
  };
  const submit = async (apply: boolean) => {
    const version = revision.current;
    setError('');
    try {
      const response = await run({
        variables: { communityId, levelId, rows, apply },
      });
      if (version !== revision.current) return;
      const report = response.data?.addressAccountImport;
      if (!Array.isArray(report?.results))
        throw new Error('No import result was returned.');
      setResults(report.results);
      setPreviewed(
        !apply &&
          !report.results.some((result: Result) => result.status === 'error'),
      );
      setCompleted(apply);
    } catch (e) {
      if (version !== revision.current) return;
      setPreviewed(false);
      setError(
        e instanceof Error
          ? e.message
          : 'Import failed. Preview again before retrying; existing accounts will be reused.',
      );
    }
  };
  if (!dbUser) return null;
  if (!allowed) return <PermissionsAlert />;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={2}>Address account import</Typography.Title>
      <Typography.Paragraph>
        Import address accounts with Direct Billing. Use CSV columns Room,
        Building and Email. Existing matching accounts are reused.
      </Typography.Paragraph>
      <Space wrap>
        <Select
          aria-label="Community"
          placeholder="Select community"
          style={{ width: 260 }}
          value={communityId}
          disabled={loading}
          onChange={(id) => {
            invalidate();
            setCommunityId(id);
            setLevelId(undefined);
          }}
          options={(corporateClients || []).map(
            (c: { corporate_client_id: number; name: string }) => ({
              value: c.corporate_client_id,
              label: c.name,
            }),
          )}
        />
        <Select
          aria-label="Membership plan"
          placeholder="Select membership plan"
          style={{ width: 260 }}
          value={levelId}
          disabled={loading || !communityId}
          loading={plansLoading}
          onChange={(id) => {
            invalidate();
            setLevelId(id);
          }}
          options={(plans?.addressImportPlans || []).map(
            (p: { id: number; name: string }) => ({
              value: p.id,
              label: p.name,
            }),
          )}
        />
        <input
          ref={input}
          aria-label="Address CSV"
          type="file"
          accept=".csv,text/csv"
          disabled={loading}
          onChange={(event) => void load(event.target.files?.[0])}
        />
      </Space>
      <Typography.Text type="secondary">
        Example: C137,Canyonview,TheGlen+CanyonviewC137@usefull.us
      </Typography.Text>
      {plansError && <Alert type="error" message={plansError.message} />}
      {error && <Alert type="error" showIcon message={error} />}
      {completed && (
        <Alert
          type={
            results.some((r) => r.status === 'error') ? 'warning' : 'success'
          }
          showIcon
          message="Import finished"
          description="Review each row below. For failed rows, preview again and retry; completed accounts will be reused."
        />
      )}
      <Space>
        <Button
          disabled={!communityId || !levelId || !rows.length || loading}
          loading={loading}
          onClick={() => void submit(false)}
        >
          Preview {rows.length ? `${rows.length} accounts` : ''}
        </Button>
        <Button
          type="primary"
          disabled={!previewed || loading}
          onClick={() => void submit(true)}
          loading={loading}
        >
          Import accounts
        </Button>
      </Space>
      <Table
        rowKey="row"
        dataSource={
          results.length
            ? results
            : rows.map((row, index) => ({
                row: index + 1,
                email: row.Email,
                status: 'Not previewed',
                message: `${row.Room} ${row.Building}`,
              }))
        }
        columns={[
          { title: 'Row', dataIndex: 'row' },
          { title: 'Email', dataIndex: 'email' },
          { title: 'Result', dataIndex: 'status' },
          { title: 'Details', dataIndex: 'message' },
          { title: 'User ID', dataIndex: 'userId' },
        ]}
        pagination={{ pageSize: 25 }}
      />
    </Space>
  );
}
